import React, { useState, useEffect } from 'react';
import {
  Container, Paper, Typography, Box, Avatar, Button, TextField,
  Grid, Card, CardContent, Divider, Alert, Chip,
  List, ListItem, ListItemText, ListItemIcon,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, Switch, FormControlLabel
} from '@mui/material';
import {
  Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon,
  Person as PersonIcon, Email as EmailIcon, Security as SecurityIcon,
  History as HistoryIcon, Analytics as AnalyticsIcon, Settings as SettingsIcon,
  Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import ModernToast from '../components/molecules/ModernToast/ModernToast';
import api from '../services/api';
import PlatformDialog from '../components/molecules/PlatformDialog/PlatformDialog';
import PlatformDialogTitle from '../components/molecules/PlatformDialog/PlatformDialogTitle';

const ProfilePage = () => {
  const { user, updateUser } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Estados do formulário
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: ''
  });

  // Estados da mudança de senha
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Estatísticas do usuário
  const [stats, setStats] = useState({
    totalArticles: 0,
    totalFavorites: 0,
    totalTags: 0,
    totalCollections: 0,
    lastLogin: null
  });

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        role: user.role || ''
      });
    }
    loadUserStats();
  }, [user]);

  const loadUserStats = async () => {
    try {
      // Usar endpoint específico para estatísticas (mais eficiente)
      const response = await api.get('/auth/stats');

      setStats({
        totalArticles: response.data.data.totalArticles || 0,
        totalFavorites: response.data.data.totalFavorites || 0,
        totalTags: response.data.data.totalTags || 0,
        totalCollections: response.data.data.totalCollections || 0,
        lastLogin: user?.lastLogin || new Date().toISOString()
      });
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
      // Fallback para valores padrão se der erro
      setStats({
        totalArticles: 0,
        totalFavorites: 0,
        totalTags: 0,
        totalCollections: 0,
        lastLogin: user?.lastLogin || new Date().toISOString()
      });
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormData({
      name: user.name || '',
      email: user.email || '',
      role: user.role || ''
    });
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      // Validações básicas no frontend
      if (!formData.name.trim()) {
        setSnackbar({
          open: true,
          message: 'Nome é obrigatório',
          severity: 'error'
        });
        return;
      }

      if (!formData.email.trim()) {
        setSnackbar({
          open: true,
          message: 'Email é obrigatório',
          severity: 'error'
        });
        return;
      }

      // Validação básica de email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        setSnackbar({
          open: true,
          message: 'Email inválido',
          severity: 'error'
        });
        return;
      }

      const response = await api.put('/auth/profile', {
        name: formData.name.trim(),
        email: formData.email.trim()
      });

      updateUser(response.data.data.user);
      setIsEditing(false);
      setSnackbar({
        open: true,
        message: 'Perfil atualizado com sucesso!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Erro ao atualizar perfil:', error);

      let errorMessage = 'Erro ao atualizar perfil';

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.errors) {
        errorMessage = error.response.data.errors.join(', ');
      } else if (error.message) {
        errorMessage = error.message;
      }

      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    try {
      if (passwordData.newPassword !== passwordData.confirmPassword) {
        setSnackbar({
          open: true,
          message: 'As senhas não coincidem',
          severity: 'error'
        });
        return;
      }

      setLoading(true);
      await api.put('/auth/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });

      setPasswordDialog(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setSnackbar({
        open: true,
        message: 'Senha alterada com sucesso!',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Erro ao alterar senha',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'error';
      case 'mensure': return 'warning';
      case 'client': return 'primary';
      default: return 'default';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'admin': return 'Administrador';
      case 'mensure': return 'Mensure';
      case 'client': return 'Cliente';
      default: return role;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        Meu Perfil
      </Typography>

      <Grid container spacing={3}>
        {/* Informações do Usuário */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Avatar sx={{ width: 80, height: 80, mr: 3, bgcolor: 'primary.main' }}>
                {user?.name?.charAt(0)?.toUpperCase() || 'U'}
              </Avatar>
              <Box>
                <Typography variant="h5" gutterBottom>
                  {user?.name || 'Usuário'}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {user?.email}
                </Typography>
                <Chip
                  label={getRoleLabel(user?.role)}
                  color={getRoleColor(user?.role)}
                  size="small"
                />
              </Box>
              <Box sx={{ ml: 'auto' }}>
                {!isEditing ? (
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={handleEdit}
                  >
                    Editar
                  </Button>
                ) : (
                  <Box>
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={handleSave}
                      disabled={loading}
                      sx={{ mr: 1 }}
                    >
                      Salvar
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<CancelIcon />}
                      onClick={handleCancel}
                    >
                      Cancelar
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nome"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  disabled={!isEditing}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  disabled={!isEditing}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Função"
                  value={getRoleLabel(formData.role)}
                  disabled
                  helperText="A função não pode ser alterada"
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 3 }}>
              <Button
                variant="outlined"
                startIcon={<SecurityIcon />}
                onClick={() => setPasswordDialog(true)}
              >
                Alterar Senha
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Estatísticas */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              <AnalyticsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Estatísticas
            </Typography>

            <List>
              <ListItem>
                <ListItemIcon>
                  <PersonIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Total de Artigos"
                  secondary={stats.totalArticles}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <HistoryIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Favoritos"
                  secondary={stats.totalFavorites}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <SettingsIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Tags Criadas"
                  secondary={stats.totalTags}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <PersonIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Coleções"
                  secondary={stats.totalCollections}
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>
      </Grid>

      {/* Dialog de Mudança de Senha (Padronizado) */}
      <PlatformDialog 
        open={passwordDialog} 
        onClose={() => setPasswordDialog(false)} 
        maxWidth="xs" 
        fullWidth
      >
        <PlatformDialogTitle
          title="Alterar Senha"
          subtitle="Sua nova senha deve ser diferente da atual."
          icon={<SecurityIcon />}
          onClose={() => setPasswordDialog(false)}
        />
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Senha Atual"
              type={showCurrentPassword ? 'text' : 'password'}
              value={passwordData.currentPassword}
              onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={() => setShowCurrentPassword(!showCurrentPassword)} edge="end" size="small">
                    {showCurrentPassword ? <VisibilityOffIcon sx={{ fontSize: 18 }} /> : <VisibilityIcon sx={{ fontSize: 18 }} />}
                  </IconButton>
                )
              }}
            />
            <TextField
              fullWidth
              label="Nova Senha"
              type={showNewPassword ? 'text' : 'password'}
              value={passwordData.newPassword}
              onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end" size="small">
                    {showNewPassword ? <VisibilityOffIcon sx={{ fontSize: 18 }} /> : <VisibilityIcon sx={{ fontSize: 18 }} />}
                  </IconButton>
                )
              }}
            />
            <TextField
              fullWidth
              label="Confirmar Nova Senha"
              type={showConfirmPassword ? 'text' : 'password'}
              value={passwordData.confirmPassword}
              onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end" size="small">
                    {showConfirmPassword ? <VisibilityOffIcon sx={{ fontSize: 18 }} /> : <VisibilityIcon sx={{ fontSize: 18 }} />}
                  </IconButton>
                )
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1.5 }}>
          <Button 
            onClick={() => setPasswordDialog(false)}
            sx={{ fontWeight: 600 }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handlePasswordChange}
            variant="contained"
            disabled={loading}
            sx={{ fontWeight: 700, borderRadius: 2, px: 3, boxShadow: 'none' }}
          >
            Confirmar Alteração
          </Button>
        </DialogActions>
      </PlatformDialog>

      {/* Snackbar para notificações */}
      <ModernToast
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        duration={4000}
        position="bottom-center"
      />
    </Container>
  );
};

export default ProfilePage;
